import UUID from "../../Utilities/Generic/UUID";
import { AGENT_CLOSE_EVENT, AGENT_PING_EVENT } from "./AgentEvents";
import type BaseWorker from "./BaseWorker";
import { WorkerObjectParser } from "./WorkerObjectParser";

export const WORKER_PORT_CLOSE_EVENT = "WORKER_PORT_CLOSE_EVENT";
export const WORKER_PORT_CALLBACK = "WORKER_PORT_CALLBACK";
export const WORKER_PORT_CLEAR_CALLBACKS = "WORKER_PORT_CLEAR_CALLBACKS";

export default class WorkerPort extends WorkerObjectParser(Object) {
    protected id: string;
    protected port: MessagePort;
    protected worker: BaseWorker;
    protected store: string;

    public onClose?: (id: string) => void;

    constructor(port: MessagePort, worker: BaseWorker) {
        super();

        this.id = UUID.generate();

        this.handleMessage = this.handleMessage.bind(this);

        this.port = port;
        this.worker = worker;
        this.store = "";

        if(typeof self !== "undefined" && typeof self.location !== "undefined") {
            this.store = self.location.host.split(".")[0];
        } else if((globalThis as unknown as { _$globalStore: string; })._$globalStore) {
            this.store = (globalThis as unknown as { _$globalStore: string; })._$globalStore;
        }

        this.port.addEventListener("message", this.handleMessage);
        this.port.addEventListener("messageerror", this.handleMessageError);
        this.port.start();
    }

    public broadcast(event: string, message: any = null): void {
        this.worker.broadcast(event, message, [ this.id ]);
    }

    public close(): void {
        this.sendMessage(WORKER_PORT_CLOSE_EVENT);
        this.port.close();
    }

    public getId(): string {
        return this.id;
    }

    protected handleUnknownMessage(e: MessageEvent): void {
        const callbacks: Record<string, Function> = {};

        const message = this.parseMessage(e.data, callbacks);

        const complete = (...args: Array<any>): void => {
            if(typeof message.success === "function") {
                message.success(...args);
            }

            this.sendMessage(WORKER_PORT_CLEAR_CALLBACKS, callbacks);
        };

        if(message.type === AGENT_PING_EVENT) {
            complete();

            return;
        }

        this.worker.handleMessage(this, message, complete);
    }

    protected handleMessage(e: MessageEvent): void {
        if(!e.data.type) {
            if(
                !e.data ||
                e.data.source === "react-devtools-detector" ||
                e.data.source === "react-devtools-content-script" ||
                e.data.source === "react-devtools-bridge" ||
                e.data.source === "react-devtools-inject-backend" ||
                typeof e.data.wappalyzer !== "undefined" ||
                e.data === "__sandbox_executing" ||
                e.data === "__sandbox_initiated" ||
                e.origin === "https://js.stripe.com" ||
                e.data === "INTEGRATION_DIALOG_CLOSE" ||
                e.data.message === "devtools-initialized"
            ) {
                // Ignore
            } else {
                console.error("Received unknown message in worker port", e);
            }

            return;
        }

        if(e.data.store) {
            this.store = e.data.store;
        }

        switch(e.data.type) {
            case AGENT_CLOSE_EVENT:
                if(typeof this.onClose === "function") {
                    this.onClose(this.id);
                }

                break;
            default:
                this.handleUnknownMessage(e);
                break;
        }
    }

    public sendMessage(event: string, message: any = null): void {
        // TODO: TEST IF PORT IS CLOSED
        this.port.postMessage({
            type : event,
            store: this.store,
            message,
        });
    }

    protected handleMessageError = (event: MessageEvent): void => {
        console.warn(
            `Could not deserialize a message and received an error (worker: ${this.worker.constructor.name})`,
            event
        );
    };
}
